import type { CurlRule, PushupRule } from './accessories.ts';
import type { MainLift, WorkoutKind } from './lifts.ts';
import type { Mass, Unit } from './mass.ts';
import type { ImportedExercise } from './import.ts';
import type { Gym } from './plates.ts';
import type { LiftRule, ReasonCode } from './progression.ts';
import type { Settings } from './settings.ts';

/** Bump when a rule change could alter a decision; stored on every workout (ADR-0002 hardening). */
export const ENGINE_VERSION = 1;

export type AccessoryId = 'curl' | 'pushup';
/** Built-in lifts and accessories, or any catalogue exercise id a hosted plan uses (ADR-0020). */
export type ExerciseId = MainLift | AccessoryId | (string & {});
/** `extra`: a set the lifter added (J20). Recorded, never counted towards progression. */
export type SetKind = 'warmup' | 'work' | 'accessory' | 'extra';

/** One planned exercise of a workout, with the reason its target was chosen. */
export interface Prescribed {
  exercise: ExerciseId;
  /** null for bodyweight (push-ups). */
  weight: Mass | null;
  sets: number;
  reps: number;
  reason: ReasonCode;
  /** Hosted plans (ADR-0020): each set's own reps, weight and role (top set, back-off, ramp…). `weight` is then the heaviest. */
  setPlan?: { reps: number; weight: Mass | null; kind: 'work' | 'top' | 'backoff' | 'ramp' | 'assistance'; /** The plan's number when the gym can't load it exactly. */ target?: Mass }[];
  /** Display name snapshot, so history never needs the catalogue. */
  name?: string;
  /** Plate maths and warm-ups apply (barbell lifts). Defaults to the built-in main lifts. */
  barbell?: boolean;
  /** false when the plan's own sets are the warm-up (e.g. Madcow ramps). */
  warmup?: boolean;
  bodyweight?: boolean;
  /** Hosted plans: the progression track this slot belongs to. */
  track?: string;
  /** J18: the plan's own exercise for this slot when a substitute is done instead (the slot's track carries on). */
  swappedFrom?: ExerciseId;
  /** Dumbbells: the weight is each hand's, never a total (BW01). */
  perHand?: boolean;
}

/** A or B for the built-in programme, or a hosted plan's workout id (A–E). */
export type AnyWorkoutKind = WorkoutKind | (string & {});

/** Which hosted plan a workout belongs to (ADR-0020). */
export interface PlanRef {
  id: string;
  version: number;
  /** The plan workout (A–E). */
  workout: string;
}

/** The settings a workout was planned with, so later edits replay under the rules of the day. */
export interface ConfigSnapshot {
  gym: Gym;
  /** Rest times the workout was planned with, so a resumed session never picks up later settings. */
  timing?: import('./session.ts').SessionTiming;
  rules: Record<MainLift, LiftRule>;
  curl?: CurlRule;
  pushup?: PushupRule;
}

interface Base {
  /** Client-generated UUIDv7 (ADR-0003). */
  id: string;
  /** Epoch milliseconds. Ordering is the array order the caller supplies (device_seq / server_seq), not this clock. */
  at: number;
}

export type RepwiseEvent =
  | (Base & {
      type: 'programme_started';
      unit: Unit;
      gym: Gym;
      startWeights: Record<MainLift, Mass>;
      curl?: { weight: Mass };
      pushup?: { sets: number; reps: number };
    })
  | (Base & {
      type: 'workout_started';
      workoutId: string;
      /** A or B for the built-in programme; a hosted plan's workout id (A–E) when `plan` is set. */
      kind: AnyWorkoutKind;
      plan?: PlanRef;
      engineVersion: number;
      prescriptions: Prescribed[];
      config: ConfigSnapshot;
    })
  | (Base & {
      type: 'set_logged';
      workoutId: string;
      exercise: ExerciseId;
      kind: SetKind;
      index: number;
      /** null = deliberately skipped. */
      reps: number | null;
      weight: Mass | null;
    })
  | (Base & { type: 'set_corrected'; setEventId: string; reps: number | null; weight: Mass | null })
  | (Base & { type: 'set_voided'; setEventId: string })
  | (Base & { type: 'workout_finished'; workoutId: string; endedEarly: boolean })
  | (Base & { type: 'workout_cancelled'; workoutId: string })
  | (Base & { type: 'workout_deleted'; workoutId: string })
  | (Base & { type: 'deload_accepted'; lift: MainLift })
  | (Base & { type: 'deload_declined'; lift: MainLift })
  | (Base & { type: 'break_restart_accepted'; fraction: number })
  | (Base & { type: 'break_restart_skipped' })
  /** Session-only events: let a live session be rebuilt exactly from the log (resume, ADR-0002). */
  | (Base & { type: 'rest_adjusted'; workoutId: string; seconds: number })
  | (Base & { type: 'rest_skipped'; workoutId: string })
  | (Base & { type: 'transition_continued'; workoutId: string })
  /** J20: do this exercise next (rack busy). Its remaining sets come first until they're done. */
  | (Base & { type: 'exercise_focused'; workoutId: string; exercise: ExerciseId })
  /** J20: one more set of an exercise, at the weight and reps given. Kind `extra`: never counts towards progression. */
  | (Base & { type: 'extra_set_added'; workoutId: string; exercise: ExerciseId; weight: Mass | null; reps: number })
  | (Base & { type: 'target_overridden'; lift: MainLift; weight: Mass })
  /**
   * ADR-0025 (FB-003): "Go lighter today". Every set of this workout not yet logged drops by `fraction` (rounded down
   * to what the plates make). `exercises` are the ones it touched: each holds its weight next time (no step up, no
   * miss). Recorded with the event so both progressions read it without re-deriving.
   */
  | (Base & { type: 'session_lightened'; workoutId: string; fraction: number; exercises: ExerciseId[] })
  /**
   * ADR-0025 (FB-002 on hosted plans): the lifter changed a plan lift's next weight on Train. From this point in the
   * log the track's value scales by to / from (the top set shown → the one chosen); the plan's rules carry on from it.
   */
  | (Base & { type: 'plan_weight_set'; selectionId: string; track: string; from: Mass; to: Mass })
  /** Complete settings snapshot; the latest in log order wins (ADR-0015). */
  | (Base & { type: 'settings_updated'; settings: Settings })
  /** US-13: a note on a workout, or on one exercise in it. The latest per (workout, exercise) wins; '' clears it. */
  | (Base & { type: 'workout_noted'; workoutId: string; exercise?: ExerciseId; note: string })
  /** US-30: bodyweight with a workout, in the programme unit at the time. */
  | (Base & { type: 'bodyweight_logged'; workoutId: string; weight: Mass; /** Absent on events written before 13 Oct 2026 (then: the unit in force). */ unit?: Unit })
  /**
   * Choose a training plan (ADR-0020, J15/J16). `planId: null` returns to the built-in StrongLifts 5×5. Inputs are
   * the starting point in the programme unit (display units). Applies from its log position forward.
   */
  | (Base & { type: 'plan_selected'; planId: string | null; version?: number; hash?: string; unit?: Unit; /** J27: adopting a newer version of the same plan; progress continues from that selection (ADR-0023). */ upgradeOf?: string; /** J16(c): going back to an earlier selection of this plan, carrying on where it left off. */ resumeOf?: string; /** Intermediate: the block's variation (plan.variations id). */ variation?: string; inputs?: Record<string, { weight: number; reps: number; rpe: number; increment?: number; onRamp?: number; rampStep?: number }> })
  /**
   * J18: do another exercise from the same family in a plan slot. It inherits the slot's progression track; `ratio` scales
   * the track's weights when the equipment differs (barbell → dumbbells). `next` = just the next workout; `ongoing` = from
   * now on (until swapped back, `to` = the plan's own exercise).
   */
  | (Base & { type: 'exercise_swapped'; /** a plan_selected id, or 'builtin' for the built-in 5×5 (track = the lift) */ selectionId: string; track: string; to: ExerciseId; scope: 'next' | 'ongoing'; ratio?: number; /** snapshot so the built-in fold needs no catalogue */ name?: string; equipment?: 'barbell' | 'dumbbells' | 'bodyweight' | 'other' })
  /** J26: progress the added weight (belt) on a bodyweight exercise: +increment after a workout with every rep. Latest wins. */
  | (Base & { type: 'added_weight_progress'; exercise: string; on: boolean })
  /** J21: a bookmarked exercise appears first on Progress. The latest per exercise wins. */
  | (Base & { type: 'exercise_bookmarked'; exercise: string; on: boolean })
  /** J19: the day the user plans the next workout (local YYYY-MM-DD). Moves nothing; holds until a workout is finished. */
  | (Base & { type: 'next_date_set'; date: string })
  /** BL01 (J25): "Keep going" after a plan's block; the end-of-block card stays dismissed for that selection. */
  | (Base & { type: 'block_continued'; selectionId: string })
  /** kg ↔ lb after onboarding (ADR-0018): current targets convert; past workouts keep their own unit. */
  | (Base & { type: 'units_changed'; from: Unit; to: Unit; settings: Settings })
  /** Reschedule: this workout comes next, until the next completed workout (US-05). */
  | (Base & { type: 'next_workout_set'; kind: AnyWorkoutKind })
  /** History from another app (ADR-0017). History only: never moves progression; weights are in the programme unit. */
  | (Base & {
      type: 'workout_imported';
      workoutId: string;
      source: 'stronglifts' | 'strong' | 'hevy' | 'manual';
      key: string;
      performedAt: number;
      name: string;
      kind: WorkoutKind | null;
      bodyweight?: Mass;
      note?: string;
      exercises: ImportedExercise[];
      /** The unit these weights are in (review B1: never inferred from log position once written). */
      unit?: Unit;
    });

export type EventOf<T extends RepwiseEvent['type']> = Extract<RepwiseEvent, { type: T }>;
